import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { List, Typography } from 'antd';
import { BenefitGroupUsages, BenefitItems } from 'all-in-one-shared-api';
import { isLocaleEnglish } from 'utils/languageUtils';

import UsageOverallItem from './UsageOverallItem';
import UsageItem from './UsageItem';

const { Title } = Typography;

const UsageGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const UsageMaxLabel = styled.div``;

const UsageGroupBody = styled.div`
  background-color: #fff;
`;

const UsageItemList = styled(List)`
  padding: 16px;
`;

export interface UsageGroupProp {
  benefitGroupUsages: BenefitGroupUsages;
}

function UsageGroup({ benefitGroupUsages }: UsageGroupProp) {
  const {
    amount,
    amountDescription,
    amountDescriptionCn,
    limit,
    limitDescription,
    limitDescriptionCn,
    groupTitle,
    groupTitleCn,
    benefitItemDetails,
  } = benefitGroupUsages;

  const { t, i18n } = useTranslation();
  const isEnglish = isLocaleEnglish(i18n.language);

  const shouldShowVisitLimit = !!(limit && parseInt(limit) > 0);
  const isGroup = !!groupTitle;

  const maxLabel = useMemo(() => {
    if (shouldShowVisitLimit) {
      return isEnglish ? limitDescription : limitDescriptionCn;
    } else {
      return isEnglish ? amountDescription : amountDescriptionCn;
    }
  }, [amountDescription, amountDescriptionCn, isEnglish, limitDescription, limitDescriptionCn, shouldShowVisitLimit]);

  return (
    <>
      <UsageGroupHeader>
        <Title level={5}>{isGroup ? (isEnglish ? groupTitle : groupTitleCn) : t('common.others')}</Title>
        <UsageMaxLabel data-testid="usage-max-label">{maxLabel}</UsageMaxLabel>
      </UsageGroupHeader>
      <UsageGroupBody>
        {isGroup && (
          <UsageOverallItem
            benefitItemDetails={benefitItemDetails ?? []}
            shouldShowVisitLimit={shouldShowVisitLimit}
            maximumValue={shouldShowVisitLimit ? parseInt(limit) : parseFloat(amount ?? '')}
          />
        )}
        <UsageItemList
          dataSource={benefitItemDetails}
          renderItem={(item, index) => <UsageItem item={item as BenefitItems} isGroupItem={isGroup} index={index} />}
        />
      </UsageGroupBody>
    </>
  );
}

export default UsageGroup;
